import { TextField } from '@mui/material';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { NumberFormatValues, PatternFormat } from 'react-number-format';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

const PHONE_FORMAT = '(###) ### - ####';

interface PhoneInputProps extends InputBaseComponentProps {
  format?: string;
  mask?: string;
  onValueChange?: (values: NumberFormatValues) => void;
}

const PhoneInputComponent = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ format = PHONE_FORMAT, mask = '_', onValueChange, ...props }, ref) => (
    <PatternFormat
      {...props}
      getInputRef={ref}
      format={format}
      mask={mask}
      onValueChange={onValueChange}
    />
  ),
);

PhoneInputComponent.displayName = 'PhoneInputComponent';

export interface InputPhoneProps extends Omit<InputComponent, 'size'> {
  format?: string;
  mask?: string;
  size?: 'small' | 'medium' | 'large';
  onChange?: (name: string, value: string) => void;
}

export function InputPhone({
  label,
  disabled,
  hidden,
  required,
  className,
  hint,
  nameOverride,
  placeholder,
  showLabel = !hidden,
  readOnly = false,
  format = PHONE_FORMAT,
  mask = '_',
  size: propSize = 'medium',
  onChange = undefined,
}: InputPhoneProps) {
  const {
    errors,
    setFieldTouched,
    setFieldValue,
    getFieldMeta,
    submitCount,
    values,
  } = useFormikContext<Record<string, string>>();

  const size = normalizeSize(propSize);

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      // validate phone number matches the format.
      if (required && value && value.toString().length < 10) {
        return `${label} must be a valid 10 digit U.S. phone number.`;
      }
      return undefined;
    },
    [label, required],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const inputPlaceholder = useMemo(
    () => placeholder ?? label?.replace(/[?:]/g, ''),
    [placeholder, label],
  );

  const handleChange = useCallback(
    (numberValues: NumberFormatValues) => {
      setFieldValue(name, numberValues.value, true);
      onChange?.(name, numberValues.value || '');
    },
    [name, setFieldValue, onChange],
  );

  // Handle setting field as touched once form submits so errors show thanks Formik
  useEffect(() => {
    const meta = getFieldMeta(name);
    if (submitCount > 0 && !meta.touched) {
      setFieldTouched(name, true);
    }
  }, [submitCount, getFieldMeta, setFieldTouched, name]);

  const value = get(values, name, '');

  return (
    <Field name={name} validate={validate}>
      {({ field: _field }: FieldProps) => (
        <TextField
          name={name}
          fullWidth
          variant="outlined"
          label={showLabel && label}
          className={className}
          disabled={disabled}
          hidden={hidden}
          required={required && !readOnly}
          placeholder={inputPlaceholder}
          slotProps={{
            inputLabel: { shrink: !!(showLabel && label) },
            input: {
              inputComponent: PhoneInputComponent,
              inputProps: {
                format,
                mask,
                onValueChange: handleChange,
                readOnly,
              },
            },
          }}
          size={size === 'large' ? 'medium' : size}
          value={value}
          error={!!errors[name]}
          helperText={errors[name] ? errors[name] : hint}
        />
      )}
    </Field>
  );
}
